import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Alert, Button, Modal, Form } from 'react-bootstrap';
import axiosInstance from "../../api/axiosConfig.jsx";

const EmployeeDashboard = () => {
    const [employees, setEmployees] = useState([]);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        try {
            const token = sessionStorage.getItem('authToken');
            if (!token) {
                throw new Error('Geen authToken gevonden');
            }

            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const companyId = tokenPayload.companyId;

            const response = await axiosInstance.get(`/employees/company/${companyId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log(response);
            setEmployees(response.data);
        } catch (err) {
            setError(err.message);
        }
    };

    const isEmployeeComplete = (employee) => {
        const requiredFields = [
            'firstName', 'lastName', 'phoneNumber', 'dateOfBirth', 'address',
            'email', 'jobTitle', 'startDate',
            'contractType', 'salary', 'hoursPerWeek', 'hoursRating'
        ];
        return requiredFields.every(field => employee[field] != null && employee[field] !== '');
    };

    const isContractExpired = (endDate) => {
        if (!endDate) return false;
        const today = new Date();
        const contractEndDate = new Date(endDate);
        return today > contractEndDate;
    };

    const getCardClass = (employee) => {
        if (isContractExpired(employee.endDate)) {
            return 'border-danger';
        }
        return isEmployeeComplete(employee) ? 'border-success' : 'border-warning';
    };

    const getHeaderClass = (employee) => {
        if (isContractExpired(employee.endDate)) {
            return 'bg-danger text-white';
        }
        return isEmployeeComplete(employee) ? 'bg-success text-white' : 'bg-warning';
    };

    const getFooterText = (employee) => {
        if (isContractExpired(employee.endDate)) {
            return 'Contract verlopen';
        }
        return isEmployeeComplete(employee) ? 'Compleet' : 'Medewerker mist belangrijke gegevens';
    };

    const handleEmployeeClick = (employee) => {
        setSelectedEmployee(employee);
        setShowModal(true);
        setIsEditing(false);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEmployee(null);
        setShowDeleteConfirmation(false);
        setIsEditing(false);
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedEmployee(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = sessionStorage.getItem('authToken');
            const updatedEmployee = {
                firstName: selectedEmployee.firstName,
                lastName: selectedEmployee.lastName,
                phoneNumber: selectedEmployee.phoneNumber,
                pincode: selectedEmployee.pincode,
                dateOfBirth: selectedEmployee.dateOfBirth,
                address: selectedEmployee.address,
                email: selectedEmployee.email,
                socialSecurityNumber: selectedEmployee.socialSecurityNumber,
                jobTitle: selectedEmployee.jobTitle,
                startDate: selectedEmployee.startDate,
                endDate: selectedEmployee.endDate,
                contractType: selectedEmployee.contractType,
                salary: parseFloat(selectedEmployee.salary),
                hoursPerWeek: parseFloat(selectedEmployee.hoursPerWeek),
                hoursRating: parseFloat(selectedEmployee.hoursRating)
            };
            await axiosInstance.put(`/employees/${selectedEmployee.employeeId}`, updatedEmployee, {
                headers: { Authorization: `Bearer ${token}` }
            });
            handleCloseModal();
            fetchEmployees();
        } catch (err) {
            setError('Fout bij het updaten van de medewerker: ' + err.message);
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirmation(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = sessionStorage.getItem('authToken');
            await axiosInstance.delete(`/employees/${selectedEmployee.employeeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            handleCloseModal();
            fetchEmployees();
        } catch (err) {
            setError('Fout bij het verwijderen van de medewerker: ' + err.message);
        }
    };

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div style={{ height: '90vh', width: 'calc(100vw - 80px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <h1 className="text-center mb-4 p-3">Medewerkers Dashboard</h1>

            <Container fluid className="flex-grow-1 overflow-auto">
                <Row className="g-4 justify-content-center">
                    {employees.map((employee) => (
                        <Col key={employee.employeeId} lg={4} md={6} className="d-flex">
                            <Card
                                className={`flex-fill ${getCardClass(employee)}`}
                                onClick={() => handleEmployeeClick(employee)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Card.Header className={getHeaderClass(employee)}>
                                    <h5 className="mb-0">{employee.firstName} {employee.lastName}</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Card.Text>
                                        <strong>Email:</strong> {employee.email || 'Niet ingevuld'}<br />
                                        <strong>Telefoon:</strong> {employee.phoneNumber || 'Niet ingevuld'}<br />
                                        <strong>Einddatum:</strong> {employee.endDate ? new Date(employee.endDate).toLocaleDateString() : 'Niet ingevuld'}<br />
                                        <strong>Contracttype:</strong> {employee.contractType || 'Niet ingevuld'}<br />
                                        <strong>Uurloon:</strong> €{employee.salary ? employee.salary.toFixed(2) : 'Niet ingevuld'}<br />
                                        <strong>Uren per week:</strong> {employee.hoursPerWeek !== undefined && employee.hoursPerWeek !== null ? employee.hoursPerWeek : 'Niet ingevuld'}
                                    </Card.Text>
                                </Card.Body>
                                <Card.Footer className={getHeaderClass(employee)}>
                                    <small>{getFooterText(employee)}</small>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Medewerker Details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }}>
                    {selectedEmployee && (
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Voornaam</Form.Label>
                                        <Form.Control type="text" name="firstName" value={selectedEmployee.firstName || ''} onChange={handleInputChange} disabled={!isEditing} required />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Achternaam</Form.Label>
                                        <Form.Control type="text" name="lastName" value={selectedEmployee.lastName || ''} onChange={handleInputChange} disabled={!isEditing} required />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control type="email" name="email" value={selectedEmployee.email || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Telefoonnummer</Form.Label>
                                <Form.Control type="tel" name="phoneNumber" value={selectedEmployee.phoneNumber || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Geboortedatum</Form.Label>
                                <Form.Control type="date" name="dateOfBirth" value={selectedEmployee.dateOfBirth || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Adres</Form.Label>
                                <Form.Control type="text" name="address" value={selectedEmployee.address || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>BSN</Form.Label>
                                <Form.Control type="text" name="socialSecurityNumber" value={selectedEmployee.socialSecurityNumber || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Functie</Form.Label>
                                <Form.Control type="text" name="jobTitle" value={selectedEmployee.jobTitle || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Startdatum</Form.Label>
                                <Form.Control type="date" name="startDate" value={selectedEmployee.startDate || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Einddatum</Form.Label>
                                <Form.Control type="date" name="endDate" value={selectedEmployee.endDate || ''} onChange={handleInputChange} disabled={!isEditing} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Contracttype</Form.Label>
                                <Form.Control type="text" name="contractType" value={selectedEmployee.contractType || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Uurloon</Form.Label>
                                <Form.Control type="number" step="0.01" name="salary" value={selectedEmployee.salary || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Uren per week</Form.Label>
                                <Form.Control type="number" step="0.5" name="hoursPerWeek" value={selectedEmployee.hoursPerWeek || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Uur rating</Form.Label>
                                <Form.Control type="number" step="0.5" name="hoursRating" value={selectedEmployee.hoursRating || ''} onChange={handleInputChange} disabled={!isEditing} required />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={() => setIsEditing(false)}>
                                Annuleren
                            </Button>
                            <Button variant="primary" onClick={handleSubmit}>
                                Opslaan
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="danger" onClick={handleDeleteClick}>
                                Verwijderen
                            </Button>
                            <Button variant="primary" onClick={handleEditClick}>
                                Bewerken
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteConfirmation} onHide={() => setShowDeleteConfirmation(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Bevestig Verwijdering</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Weet u zeker dat u deze medewerker wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
                        Annuleren
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>
                        Verwijderen
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EmployeeDashboard;